/**
 * Tag-Drift Detective — proactive tag-discipline monitoring
 * ============================================================
 * Closes the visibility gap that 82% of FinOps practitioners say is
 * unsolved (per Cloudaware/Usage.ai 2026). Daily delta vs 7-day
 * baseline + top untaggers + cost on the line.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Tags, ArrowLeft, RefreshCcw, Loader2, TrendingUp, TrendingDown,
  AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtMoney = (n) => {
  if (!n && n !== 0) return '$0';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${(a / 1_000).toFixed(1)}K`;
  return `$${Math.round(a)}`;
};

const TREND_META = {
  worsening: { icon: TrendingDown, color: 'text-rose-500', bg: 'bg-rose-500/10 border-rose-500/30', label: 'Worsening — discipline slipping' },
  stable:    { icon: ShieldCheck,  color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'Stable — keep it up' },
  improving: { icon: TrendingUp,   color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'Improving — nice work' },
};

const TagDrift = () => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tag-drift/snapshot`, auth());
      setSnap(data?.available ? data : null);
    } catch (e) {
      console.debug('tag-drift snapshot unavailable:', e?.message);
      setSnap(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/tag-drift/run`, {}, auth());
      setSnap(data);
      toast.success('Snapshot recomputed');
    } catch (e) {
      toast.error('Could not run', { description: e?.response?.data?.detail || e?.message });
    } finally { setRunning(false); }
  };

  const trend = snap && (TREND_META[snap.trend] || TREND_META.stable);
  const TrendIcon = trend?.icon;

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="tag-drift-page">
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="text-sm text-slate-300 hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Tags className="w-7 h-7 text-emerald-500" />
            Tag-Drift Detective
          </h1>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Daily check on whether your tagging discipline is slipping. Catches the silent decay
            before it breaks chargeback, cost allocation, or the CFO's monthly close.
          </p>
        </div>
        <Button onClick={runNow} disabled={running} variant="default"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="run-now-btn">
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />}
          Run snapshot
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : !snap ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-300 space-y-3">
            <AlertTriangle className="w-8 h-8 mx-auto text-amber-500" />
            <p>No snapshot yet. Hit <span className="text-emerald-500 font-medium">Run snapshot</span> above to compute the first one.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Trend banner */}
          <Card className={`border ${trend.bg}`} data-testid="trend-banner">
            <CardContent className="p-5 flex items-center gap-4">
              {TrendIcon && <TrendIcon className={`w-8 h-8 ${trend.color}`} />}
              <div className="flex-1">
                <p className="font-semibold">{trend.label}</p>
                <p className="text-sm text-slate-300">
                  This week <span className="font-mono text-foreground">{snap.this_week.untagged_pct}%</span> of new resources are untagged ·{' '}
                  baseline 7d ago was <span className="font-mono">{snap.baseline_7d_ago.untagged_pct}%</span> ·{' '}
                  delta <span className={`font-mono font-bold ${trend.color}`}>{snap.delta_pp >= 0 ? '+' : ''}{snap.delta_pp}pp</span>
                </p>
              </div>
              <Badge variant="outline" className="text-xs">For {snap.for_date}</Badge>
            </CardContent>
          </Card>

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpis">
            {[
              { label: 'New resources this week', val: snap.this_week.total_resources.toLocaleString(), accent: 'text-foreground' },
              { label: 'Untagged this week',      val: snap.this_week.untagged_resources.toLocaleString(), accent: snap.this_week.untagged_resources > 0 ? 'text-amber-500' : 'text-emerald-500' },
              { label: 'Untagged % this week',    val: `${snap.this_week.untagged_pct}%`, accent: snap.this_week.untagged_pct > 15 ? 'text-amber-500' : 'text-emerald-500' },
              { label: 'Cost on the line',        val: `${fmtMoney(snap.this_week.untagged_monthly_cost_usd)}/mo`, accent: snap.this_week.untagged_monthly_cost_usd > 0 ? 'text-amber-500' : 'text-emerald-500' },
            ].map((s, i) => (
              <Card key={i}><CardContent className="p-4">
                <p className="text-[11px] uppercase tracking-widest text-slate-300 mb-1">{s.label}</p>
                <p className={`text-2xl font-bold tabular-nums ${s.accent}`}>{s.val}</p>
              </CardContent></Card>
            ))}
          </div>

          {/* Top untaggers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top untaggers this week</CardTitle>
              <CardDescription>The IAM identities, terraform workspaces or owners creating the most untagged resources.</CardDescription>
            </CardHeader>
            <CardContent data-testid="top-untaggers">
              {(!snap.top_untaggers || snap.top_untaggers.length === 0) ? (
                <p className="text-sm text-slate-300 py-4">No untagged resources detected this week — clean run.</p>
              ) : (
                <div className="space-y-2">
                  {snap.top_untaggers.map((u, i) => (
                    <div key={u.owner} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`untagger-${i}`}>
                      <span className="text-slate-300 tabular-nums w-7 text-right">{i + 1}.</span>
                      <code className="font-semibold truncate">{u.owner}</code>
                      <Badge variant="outline" className="text-[11px]">{u.untagged_count} untagged</Badge>
                      <div className="flex-1" />
                      <span className="font-mono text-amber-500 font-semibold">{fmtMoney(u.monthly_cost_usd)}/mo</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Required tags */}
          <Card>
            <CardHeader><CardTitle className="text-base">Required tags</CardTitle><CardDescription>A resource is untagged if any of these are missing.</CardDescription></CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {(snap.required_tags || []).map((t) => (
                  <Badge key={t} variant="outline" className="font-mono">{t}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TagDrift;
